import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function LoginPage() {
  const { loginOwner, loginStaff } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState(searchParams.get('tab') === 'staff' ? 'staff' : 'owner');
  const [ownerForm, setOwnerForm] = useState({ email: '', password: '' });
  const [staffForm, setStaffForm] = useState({ slug: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleOwnerChange(e) {
    setOwnerForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }

  function handleStaffChange(e) {
    setStaffForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }

  function switchTab(newTab) {
    setTab(newTab);
    setError('');
  }

  async function handleOwnerSubmit(e) {
    e.preventDefault();
    if (!ownerForm.email || !ownerForm.password) { setError('Email and password are required.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/owner/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ownerForm),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed.'); return; }
      loginOwner(data.token, data.business);
      navigate('/dashboard');
    } catch {
      setError('Network error. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }

  async function handleStaffSubmit(e) {
    e.preventDefault();
    if (!staffForm.slug || !staffForm.email || !staffForm.password) {
      setError('All fields are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/staff/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(staffForm),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed.'); return; }
      loginStaff(data.token, data.employee);
      navigate('/dashboard');
    } catch {
      setError('Network error. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="main-full">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <h1>Roster<span>AI</span></h1>
            <p>Smart restaurant staff scheduling</p>
          </div>

          <Link to="/help" className="btn-get-started">
            📖 Get Started Guide
          </Link>

          <div className="auth-tabs">
            <button
              type="button"
              className={`auth-tab${tab === 'owner' ? ' active' : ''}`}
              onClick={() => switchTab('owner')}
            >
              Owner
            </button>
            <button
              type="button"
              className={`auth-tab${tab === 'staff' ? ' active' : ''}`}
              onClick={() => switchTab('staff')}
            >
              Staff
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {tab === 'owner' ? (
            <form onSubmit={handleOwnerSubmit}>
              <p className="auth-subtitle">Sign in to manage your restaurant roster</p>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={ownerForm.email}
                  onChange={handleOwnerChange}
                  placeholder="owner@myrestaurant.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="form-control"
                  name="password"
                  value={ownerForm.password}
                  onChange={handleOwnerChange}
                  placeholder="Your password"
                  autoComplete="current-password"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Sign In'}
              </button>
              <div className="auth-footer">
                <Link to="/forgot-password">Forgot password?</Link>
                <br />
                No account? <Link to="/register">Register your restaurant</Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleStaffSubmit}>
              <p className="auth-subtitle">Sign in to view your shifts and availability</p>
              <div className="form-group">
                <label>Restaurant Code</label>
                <input
                  type="text"
                  className="form-control"
                  name="slug"
                  value={staffForm.slug}
                  onChange={handleStaffChange}
                  placeholder="e.g. sushi-place"
                  autoComplete="off"
                  required
                />
                <div className="form-hint">Ask your manager for the restaurant code</div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={staffForm.email}
                  onChange={handleStaffChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="form-control"
                  name="password"
                  value={staffForm.password}
                  onChange={handleStaffChange}
                  placeholder="Your password"
                  autoComplete="current-password"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Sign In'}
              </button>
              <div className="auth-footer">
                <Link to="/forgot-password?role=staff">Forgot password?</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
